import { trackError } from '@local/metrics';
import type { SelectMenuItem } from '@local/web-design-system/components/SelectMenu';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import { useContext, useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { Navigate } from 'react-router';

import type { Discovery } from '../../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import {
    useLoadLicenseOrgsQuery,
    useLoadLicenseTokenQuery,
} from '../../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import type {
    AccessAndIDTokenWithExpiresAt as AccessToken,
    IdTokenDecoded,
} from '../../../apiClients/types';
import { LOGIN_ERROR_ROUTE, LOGIN_ROUTE } from '../../../constants';
import { getCombinedToken as getAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import type {
    Hub,
    Org,
} from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { setCurrentEvoInstance } from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { Background } from '../../background/Background';
import { LoginContext } from '../../loginHandler/LoginContext';
import { OrgHubSelectMenu } from '../OrgHubSelectMenu/OrgHubSelectMenu';
import { useStyles } from '../sharedSelector.styles';

interface OptionsGroup {
    label: string;
    optionIds: string[];
}

// Maps the list of orgs and their related hubs for the selectMenu display.
function evoOptionsGroups(
    data: Discovery | undefined,
    evoMap: Map<string, { org: Org; hub: Hub }>,
): OptionsGroup[] {
    const response: OptionsGroup[] = [];
    if (data) {
        [...data.organizations]
            .sort((a: Org, b: Org) => (a.display_name > b.display_name ? 1 : -1))
            .forEach((org: Org) => {
                const hubs = [];
                for (const entry of evoMap.entries()) {
                    if (entry[1].org.id === org.id) {
                        hubs.push(entry[1].hub);
                    }
                }
                if (hubs.length > 0) {
                    response.push({
                        label: org.display_name,
                        optionIds: hubs
                            .sort((a, b) => (a.display_name > b.display_name ? 1 : -1))
                            .map((hub) => `${org.id}-${hub.code}`),
                    });
                }
            });
    }
    return response;
}

function generateEvoMap(
    data: Discovery | undefined,
    service: string,
): Map<string, { org: Org; hub: Hub }> {
    const map = new Map<string, { org: Org; hub: Hub }>();
    if (data) {
        const hubs = new Map<string, Hub>();
        for (const hub of data.hubs) {
            hubs.set(hub.code, hub);
        }
        const orgs = new Map<string, Org>();
        for (const org of data.organizations) {
            orgs.set(org.id, org);
        }
        for (const sa of data.service_access) {
            const hub = hubs.get(sa.hub_code);
            const org = orgs.get(sa.org_id);
            if (sa.services.includes(service) && hub && org) {
                map.set(`${sa.org_id}-${sa.hub_code}`, { org, hub });
            }
        }
    }
    return map;
}

function instances(data: Map<string, { org: Org; hub: Hub }>): SelectMenuItem[] {
    const items = [];
    for (const entry of data.entries()) {
        items.push({ key: entry[0], label: entry[1].hub.display_name });
    }
    return items.sort();
}

function getDecodedIDToken(accessToken: AccessToken | null) {
    if (accessToken && accessToken.id_token) {
        return decodeToken<IdTokenDecoded>(accessToken.id_token);
    }
    return null;
}

export function OrgHubSelector({ homePagePath = '/' }: { homePagePath?: string }) {
    const { service } = useContext(LoginContext);
    const evoData = useLoadLicenseOrgsQuery({ service });
    const { classes } = useStyles();
    const evoMap = generateEvoMap(evoData.data, service);

    const [selectedInstance, setSelectedInstance] = useState<string>('');
    const [fetchToken, setFetchToken] = useState<boolean>(false);

    const evoInstance =
        selectedInstance && evoMap.has(selectedInstance) ? evoMap.get(selectedInstance) : undefined;
    const evoToken = useLoadLicenseTokenQuery(
        fetchToken && evoInstance
            ? {
                  service,
              }
            : skipToken,
    );
    useEffect(() => {
        // Only one license, use that one.
        if (evoMap.size === 1 && !fetchToken) {
            const [key, instance] = [...evoMap.entries()][0];
            setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
            setSelectedInstance(key);
            setFetchToken(true);
        }
    }, [evoMap]);

    const accessToken = getAccessToken();
    const decodedToken = getDecodedIDToken(accessToken);
    const userName = decodedToken ? `${decodedToken?.given_name} ${decodedToken?.family_name}` : '';
    const expired = accessToken && accessToken.expires_at <= Date.now();

    if (expired) {
        // Scenario where user sits on org selector page for 24 hours
        return <Navigate to={LOGIN_ROUTE} />;
    }

    if (evoData.error) {
        trackError(
            'Error loading instances',
            `Error loading instances for ${service} in <OrgSelector>`,
            {
                evo: evoData.error,
            },
        );
        // If we can't get the list of service instances, return the user to the login page.
        return <Navigate to={`${LOGIN_ERROR_ROUTE}?error=entitlements_failure`} />;
    }

    if (evoToken.error) {
        trackError(
            'Error loading evo token',
            `Error loading token for ${service} in <OrgSelector>`,
            {
                evoToken: evoToken.error,
            },
        );
        // If we can't get the token for the given instance, return the user to the login page.
        return <Navigate to={`${LOGIN_ERROR_ROUTE}?error=entitlements_failure`} />;
    }

    if (evoData.isLoading || evoToken.isLoading) {
        return (
            <Background>
                <Grid
                    item
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100px',
                    }}
                    alignContent="center"
                    justifyContent="center"
                    display="grid"
                >
                    <CircularProgress automation-id="spinner-loading" />
                </Grid>
            </Background>
        );
    }

    async function selectInstance(): Promise<void> {
        if (!selectedInstance) {
            return;
        }
        const instance = evoMap.get(selectedInstance);
        if (!instance) {
            return;
        }
        setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
        setFetchToken(true);
    }

    // No licenses, skip and move on.
    if (evoData.data?.service_access.length === 0) {
        setCurrentEvoInstance({ hasNoLicense: true });
        return <Navigate to={homePagePath} />;
    }
    if (fetchToken && evoToken.data) {
        return <Navigate to={homePagePath} />;
    }

    const optionGroups = evoOptionsGroups(evoData.data, evoMap);
    const selected = evoData.data ? optionGroups[0].optionIds[0] : '';

    return (
        <Background>
            <Grid item xs={12} className={classes.formContainer}>
                <div className={classes.label}>
                    <Typography>Name: {userName || null}</Typography>
                    <Grid item xs className={classes.selectMenu}>
                        <OrgHubSelectMenu
                            options={instances(evoMap)}
                            optionGroups={optionGroups}
                            onSelected={(key) => setSelectedInstance(key)}
                            defaultValue={selected}
                            width={360}
                        />
                    </Grid>
                </div>
                <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    className={classes.button}
                    onClick={() => selectInstance()}
                    disabled={!selectedInstance}
                    id="continue-btn"
                >
                    Continue
                </Button>
            </Grid>
        </Background>
    );
}
